export interface IChartOfAccount {
    id: number;
    accountCode: string;
    accountName: string;
    accountType: AccountType;
    accountCategory: AccountCategory;
    parentAccountCode?: string;
    isActive: boolean;
    isSystem: boolean;
    description?: string;
    openingBalance: number;
    currentBalance: number;
    createdDate: string;
    modifiedDate?: string;
    // Navigation properties
    parentAccount?: IChartOfAccount;
    subAccounts: IChartOfAccount[];
}

export interface ICreateChartOfAccount {
    accountCode: string;
    accountName: string;
    accountType: AccountType;
    accountCategory: AccountCategory;
    parentAccountCode?: string;
    description?: string;
    openingBalance: number;
}

export interface IJournalEntry {
    id: number;
    journalNumber: string;
    transactionDate: string;
    postingDate: string;
    description: string;
    reference?: string;
    source: TransactionSource;
    sourceDocumentId?: number;
    sourceDocumentType?: string;
    totalDebits: number;
    totalCredits: number;
    status: JournalEntryStatus;
    isReversed: boolean;
    reversalJournalId?: number;
    createdDate: string;
    createdBy: string;
    postedDate?: string;
    postedBy?: string;
    notes?: string;
    ledgerEntries: IGeneralLedgerEntry[];
}

export interface IGeneralLedgerEntry {
    id: number;
    journalEntryId: number;
    accountCode: string;
    transactionDate: string;
    postingDate: string;
    debitAmount: number;
    creditAmount: number;
    description: string;
    reference?: string;
    source: TransactionSource;
    sourceDocumentId?: number;
    sourceDocumentType?: string;
    isReversed: boolean;
    reversalEntryId?: number;
    createdDate: string;
    createdBy: string;
    // Navigation properties
    journalEntry?: IJournalEntry;
    account?: IChartOfAccount;
}

export interface ICreateJournalEntry {
    description: string;
    transactionDate: string;
    reference?: string;
    notes?: string;
    entries: ICreateGeneralLedgerEntry[];
}

export interface ICreateGeneralLedgerEntry {
    accountCode: string;
    debitAmount: number;
    creditAmount: number;
    description: string;
}

export interface ITrialBalance {
    accountCode: string;
    accountName: string;
    accountType: AccountType;
    debitBalance: number;
    creditBalance: number;
}

export interface IAccountBalance {
    id: number;
    accountCode: string;
    balanceDate: string;
    openingBalance: number;
    debitMovements: number;
    creditMovements: number;
    closingBalance: number;
    lastUpdated: string;
    account?: IChartOfAccount;
}

export interface IIncomeStatementLine {
    accountCode: string;
    accountName: string;
    amount: number;
}

export interface IIncomeStatement {
    fromDate: string;
    toDate: string;
    revenueAccounts: IIncomeStatementLine[];
    expenseAccounts: IIncomeStatementLine[];
    totalRevenue: number;
    totalExpenses: number;
    netIncome: number;
}

export enum AccountType {
    Asset = 0,
    Liability = 1,
    Equity = 2,
    Revenue = 3,
    Expense = 4
}

export enum AccountCategory {
    // Assets
    CurrentAssets = 0,
    FixedAssets = 1,
    IntangibleAssets = 2,
    
    // Liabilities
    CurrentLiabilities = 3,
    LongTermLiabilities = 4,
    
    // Equity
    OwnersEquity = 5,
    RetainedEarnings = 6,
    
    // Revenue
    Sales = 7,
    ServiceRevenue = 8,
    OtherRevenue = 9,
    
    // Expenses
    CostOfGoodsSold = 10,
    OperatingExpenses = 11,
    FinancingExpenses = 12,
    TaxExpenses = 13
}

export enum TransactionSource {
    Manual = 0,
    Sales = 1,
    Purchase = 2,
    Inventory = 3,
    Payroll = 4,
    FixedAssets = 5,
    BankTransfer = 6,
    Adjustment = 7,
    YearEndClose = 8
}

export enum JournalEntryStatus {
    Draft = 0,
    Posted = 2,
    Cancelled = 1
}

// Helper functions for enums
export const getAccountTypeLabel = (type: AccountType): string => {
    switch (type) {
        case AccountType.Asset: return 'Asset';
        case AccountType.Liability: return 'Liability';
        case AccountType.Equity: return 'Equity';
        case AccountType.Revenue: return 'Revenue';
        case AccountType.Expense: return 'Expense';
        default: return 'Unknown';
    }
};

export const getAccountCategoryLabel = (category: AccountCategory): string => {
    switch (category) {
        case AccountCategory.CurrentAssets: return 'Current Assets';
        case AccountCategory.FixedAssets: return 'Fixed Assets';
        case AccountCategory.IntangibleAssets: return 'Intangible Assets';
        case AccountCategory.CurrentLiabilities: return 'Current Liabilities';
        case AccountCategory.LongTermLiabilities: return 'Long Term Liabilities';
        case AccountCategory.OwnersEquity: return "Owner's Equity";
        case AccountCategory.RetainedEarnings: return 'Retained Earnings';
        case AccountCategory.Sales: return 'Sales';
        case AccountCategory.ServiceRevenue: return 'Service Revenue';
        case AccountCategory.OtherRevenue: return 'Other Revenue';
        case AccountCategory.CostOfGoodsSold: return 'Cost of Goods Sold';
        case AccountCategory.OperatingExpenses: return 'Operating Expenses';
        case AccountCategory.FinancingExpenses: return 'Financing Expenses';
        case AccountCategory.TaxExpenses: return 'Tax Expenses';
        default: return 'Unknown';
    }
};

export const getTransactionSourceLabel = (source: TransactionSource): string => {
    switch (source) {
        case TransactionSource.Manual: return 'Manual';
        case TransactionSource.Sales: return 'Sales';
        case TransactionSource.Purchase: return 'Purchase';
        case TransactionSource.Inventory: return 'Inventory';
        case TransactionSource.Payroll: return 'Payroll';
        case TransactionSource.FixedAssets: return 'Fixed Assets';
        case TransactionSource.BankTransfer: return 'Bank Transfer';
        case TransactionSource.Adjustment: return 'Adjustment';
        case TransactionSource.YearEndClose: return 'Year End Close';
        default: return 'Unknown';
    }
};

export const getJournalEntryStatusLabel = (status: JournalEntryStatus): string => {
    switch (status) {
        case JournalEntryStatus.Draft: return 'Draft';
        case JournalEntryStatus.Posted: return 'Posted';
        case JournalEntryStatus.Cancelled: return 'Cancelled';
        default: return 'Unknown';
    }
};

// Customer/Vendor Accounting interfaces
export interface ICustomerAccountingEntry {
    id: number;
    transactionDate: string;
    postingDate: string;
    debitAmount: number;
    creditAmount: number;
    description: string;
    reference?: string;
    source: TransactionSource;
    sourceDocumentType?: string;
    sourceDocumentId?: number;
    journalNumber?: string;
    journalStatus?: string;
    balance: number;
}

export interface ICustomerAccountBalance {
    customerId: number;
    customerName: string;
    accountCode: string;
    accountName?: string;
    balance: number;
    asOfDate: string;
    lastUpdated?: string;
    message?: string;
}

export interface ICustomerAccountSummary {
    customer: {
        id: number;
        name: string;
        tpin?: string;
    };
    account: {
        code: string;
        name: string;
        currentBalance: number;
        exists: boolean;
    };
    recentActivity: {
        period: string;
        transactionCount: number;
        totalDebits: number;
        totalCredits: number;
        netChange: number;
    };
    recentTransactions: Array<{
        date: string;
        description: string;
        debit: number;
        credit: number;
        reference?: string;
        source: string;
    }>;
}

export interface IVendorAccountingEntry {
    id: number;
    transactionDate: string;
    postingDate: string;
    debitAmount: number;
    creditAmount: number;
    description: string;
    reference?: string;
    source: TransactionSource;
    sourceDocumentType?: string;
    sourceDocumentId?: number;
    journalNumber?: string;
    journalStatus?: string;
    balance: number;
}

export interface IVendorAccountBalance {
    vendorId: number;
    vendorName: string;
    accountCode: string;
    accountName?: string;
    balance: number;
    asOfDate: string;
    lastUpdated?: string;
    message?: string;
}

export interface IVendorAccountSummary {
    vendor: {
        id: number;
        name: string;
        tradeName?: string;
    };
    account: {
        code: string;
        name: string;
        currentBalance: number;
        exists: boolean;
    };
    recentActivity: {
        period: string;
        transactionCount: number;
        totalDebits: number;
        totalCredits: number;
        netChange: number;
    };
    recentTransactions: Array<{
        date: string;
        description: string;
        debit: number;
        credit: number;
        reference?: string;
        source: string;
    }>;
}